import React, { useState, useEffect, useRef } from 'react'
import { ChevronDown, Search, Check } from 'lucide-react'
import ReactCountryFlag from 'react-country-flag'
import { SUPPORTED_COUNTRIES } from '../countries'

type CountryStepProps = {
    selectedCountryEnum: string | null
    setSelectedCountryEnum: (enumName: string) => void
}

const CountryStep: React.FC<CountryStepProps> = ({ selectedCountryEnum, setSelectedCountryEnum }) => {
    const [dropdownSearch, setDropdownSearch] = useState('')
    const [isDropdownOpen, setIsDropdownOpen] = useState(false)
    const dropdownRef = useRef<HTMLDivElement>(null)

    const selectedCountry = SUPPORTED_COUNTRIES.find(c => c.enumName === selectedCountryEnum)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    const dropdownFilteredCountries = SUPPORTED_COUNTRIES.filter(country =>
        country.name.toLowerCase().includes(dropdownSearch.toLowerCase())
    )

    return (
        <div className="relative w-full" ref={dropdownRef}>
            <button
                type="button"
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="w-full flex items-center justify-between gap-2 bg-white dark:bg-neutral-900 hover:bg-neutral-100 dark:hover:bg-neutral-800 border border-neutral-200 dark:border-neutral-700/80 rounded-lg px-2.5 py-1 text-xs font-semibold text-neutral-900 dark:text-white transition-all shadow-2xs"
            >
                <div className="flex items-center gap-2 truncate">
                    {selectedCountry ? (
                        <>
                            <ReactCountryFlag svg countryCode={selectedCountry.code} style={{ width: '1.2em', height: '1.2em', flexShrink: 0, borderRadius: '2px' }} />
                            <span className="truncate font-medium text-[11px] text-neutral-800 dark:text-neutral-200">{selectedCountry.name}</span>
                        </>
                    ) : (
                        <span className="text-neutral-400 text-[11px] font-normal">Select country...</span>
                    )}
                </div>
                <ChevronDown size={13} className={`text-neutral-400 transition-transform duration-200 flex-shrink-0 ${isDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDropdownOpen && (
                <div className="absolute right-0 top-full mt-1 w-56 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl shadow-2xl z-50 overflow-hidden flex flex-col backdrop-blur-md">
                    <div className="p-2 border-b border-neutral-200 dark:border-neutral-800 bg-neutral-50/50 dark:bg-neutral-800/40">
                        <div className="relative">
                            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-400" />
                            <input
                                type="text"
                                placeholder="Search country..."
                                value={dropdownSearch}
                                onChange={(e) => setDropdownSearch(e.target.value)}
                                className="w-full bg-white dark:bg-neutral-950 border border-neutral-200 dark:border-neutral-700/80 rounded-md pl-7 pr-2 py-1 text-[11px] text-neutral-900 dark:text-white focus:outline-none focus:border-blue-500"
                                autoFocus
                            />
                        </div>
                    </div>
                    <div className="max-h-44 overflow-y-auto p-1 flex flex-col gap-0.5">
                        {dropdownFilteredCountries.length > 0 ? (
                            dropdownFilteredCountries.map(country => {
                                const isSelected = selectedCountry?.enumName === country.enumName
                                return (
                                    <div
                                        key={country.enumName}
                                        onClick={() => {
                                            setSelectedCountryEnum(country.enumName)
                                            setIsDropdownOpen(false)
                                            setDropdownSearch('')
                                        }}
                                        className={`flex items-center justify-between px-2.5 py-1.5 rounded-md text-[11px] cursor-pointer transition-all ${
                                            isSelected
                                                ? 'bg-blue-500/10 text-blue-500 font-bold'
                                                : 'hover:bg-neutral-100 dark:hover:bg-neutral-800/60 text-neutral-700 dark:text-neutral-200'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2 truncate">
                                            <ReactCountryFlag svg countryCode={country.code} style={{ width: '1.1em', height: '1.1em', flexShrink: 0, borderRadius: '2px' }} />
                                            <span className="truncate">{country.name}</span>
                                        </div>
                                        {isSelected && <Check size={12} className="text-blue-500 flex-shrink-0" />}
                                    </div>
                                )
                            })
                        ) : (
                            <div className="p-3 text-center text-[11px] text-neutral-400">No country found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default CountryStep